import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowRight, Anchor, ShieldCheck, Sparkles, Receipt, BarChart3, Banknote,
    FileText, Globe2, Lock, Users, Zap, ScanLine, Coins, Mail, Repeat,
    CheckSquare, Paperclip,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import SalesAgentWidget from "@/components/SalesAgentWidget";

const FEATURES = [
    { icon: ScanLine, title: "Snap a bill, book it", desc: "Upload a supplier bill or receipt — PDF or photo, multi-page supported. GPT-4o Vision reads the vendor, dates, line items and totals, matches or creates the vendor, and opens a pre-filled draft for a one-click save." },
    { icon: Coins, title: "Multi-currency, properly done", desc: "Price invoices, bills, and expenses in any currency. Live FX rates auto-fill, you can override them, and the rate is locked on save so every report rolls up in your base currency without drift." },
    { icon: FileText, title: "Invoices that get paid faster", desc: "Branded templates, recurring schedules with auto-email, one-click payment receipts via Mailgun, and a paid invoice history that stays squeaky clean." },
    { icon: Receipt, title: "Expenses & bills with receipts attached", desc: "Every money-out transaction can carry a compressed, org-scoped receipt image or PDF — linked permanently for audit and clickable from every list." },
    { icon: Banknote, title: "Banking, reconciled", desc: "Multiple accounts, CSV statement import with auto-categorisation, duplicate detection, and a focused reconciliation queue." },
    { icon: BarChart3, title: "Reports your accountant loves", desc: "P&L, Balance Sheet, Cash Flow, Aged AR/AP, Trial Balance — all computed on base-currency totals and exportable as CSV." },
    { icon: CheckSquare, title: "Bulk actions without the spreadsheet", desc: "Multi-select invoices, bills, customers, or vendors and act on all of them at once. Delete cascades to linked payments with an honest preview of what will change." },
    { icon: ShieldCheck, title: "Real RBAC & 2FA out of the box", desc: "Owner, Admin, Accountant, Manager, Staff, Auditor — plus mandatory email 2FA on login, trusted-device cookies, and a signed audit log for every mutation." },
    { icon: Globe2, title: "Built for Cayman, designed for the world", desc: "Switch jurisdictions, currencies, and tax engines as you grow — VAT, sales tax, or no tax at all." },
];

export default function Landing() {
    return (
        <div className="min-h-screen bg-ocean-mesh text-ocean-700">
            {/* Nav */}
            <header className="sticky top-0 z-30 glass-panel">
                <div className="max-w-7xl mx-auto h-16 px-6 flex items-center justify-between">
                    <Logo />
                    <nav className="hidden md:flex items-center gap-8 text-sm text-ocean-700/80">
                        <a href="#features" className="hover:text-ocean-700 transition-colors">Features</a>
                        <a href="#whats-new" className="hover:text-ocean-700 transition-colors">What's new</a>
                        <a href="#cayman" className="hover:text-ocean-700 transition-colors">Cayman mode</a>
                        <Link to="/pricing" className="hover:text-ocean-700 transition-colors">Pricing</Link>
                    </nav>
                    <div className="flex items-center gap-2">
                        <Link to="/login"><Button variant="ghost" data-testid="nav-signin">Sign in</Button></Link>
                        <Link to="/register"><Button className="bg-ocean-700 hover:bg-ocean-700/90 text-white rounded-xl" data-testid="nav-getstarted">Get started <ArrowRight className="h-4 w-4 ml-1.5" /></Button></Link>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 pt-16 pb-24 lg:pt-28 lg:pb-36 grid lg:grid-cols-12 gap-12 items-center">
                    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-7">
                        <Badge className="mb-6 bg-coral/10 text-coral-dark border-0 rounded-full px-3 py-1 text-xs">
                            <Anchor className="h-3 w-3 mr-1.5 inline-block" /> Born in the Cayman Islands · Built for the world
                        </Badge>
                        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] text-ocean-700 font-medium">
                            Accounting that feels like<br />
                            <span className="gradient-text">an Island Breeze.</span>
                        </h1>
                        <p className="mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed">
                            BlueLedger is the modern bookkeeping platform for freelancers, founders, and finance teams — with first-class Cayman Islands jurisdiction support, no-tax mode, and a UI you'll actually want to open every morning.
                        </p>
                        <div className="mt-10 flex flex-wrap items-center gap-3">
                            <Link to="/register">
                                <Button size="lg" className="bg-ocean-700 hover:bg-ocean-700/90 text-white rounded-xl px-7 h-12" data-testid="hero-cta-start">
                                    Start free <ArrowRight className="h-4 w-4 ml-1.5" />
                                </Button>
                            </Link>
                            <Link to="/pricing">
                                <Button size="lg" variant="outline" className="rounded-xl px-7 h-12 border-sand-300" data-testid="hero-cta-pricing">
                                    See pricing
                                </Button>
                            </Link>
                            <span className="text-xs text-muted-foreground ml-1">No credit card · Free forever plan</span>
                        </div>
                        <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
                            {[
                                { v: "0%", l: "Cayman corporate tax" },
                                { v: "$0", l: "to start with Free plan" },
                                { v: "5 min", l: "to your first invoice" },
                            ].map((s) => (
                                <div key={s.l}>
                                    <div className="font-heading text-2xl text-ocean-700 font-medium">{s.v}</div>
                                    <div className="text-xs text-muted-foreground mt-1">{s.l}</div>
                                </div>
                            ))}
                        </div>
                    </motion.div>

                    {/* Hero visual: dashboard mock */}
                    <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }} className="lg:col-span-5">
                        <div className="relative">
                            <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-secondary/20 via-secondary/5 to-transparent blur-2xl" />
                            <div className="relative rounded-3xl bg-white shadow-card border border-sand-200 overflow-hidden">
                                <div className="bg-deep-ocean p-5 text-white">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="text-xs uppercase tracking-[0.18em] text-white/60">Cash position</div>
                                            <div className="text-3xl font-heading font-medium mt-1">KYD 84,320.50</div>
                                        </div>
                                        <Badge className="bg-coral/20 text-coral-light border-0">+12.4% MoM</Badge>
                                    </div>
                                    <svg viewBox="0 0 320 80" className="mt-4 w-full text-white/80">
                                        <defs>
                                            <linearGradient id="g1" x1="0" x2="0" y1="0" y2="1">
                                                <stop offset="0" stopColor="rgba(0,163,181,0.6)" />
                                                <stop offset="1" stopColor="rgba(0,163,181,0)" />
                                            </linearGradient>
                                        </defs>
                                        <path d="M0 60 L40 50 L80 55 L120 35 L160 42 L200 25 L240 30 L280 12 L320 18 L320 80 L0 80 Z" fill="url(#g1)" />
                                        <path d="M0 60 L40 50 L80 55 L120 35 L160 42 L200 25 L240 30 L280 12 L320 18" fill="none" stroke="rgba(0,163,181,1)" strokeWidth="2" />
                                    </svg>
                                </div>
                                <div className="p-5 grid grid-cols-2 gap-4">
                                    {[
                                        { l: "Revenue MTD", v: "KYD 12,450", c: "text-ocean-700" },
                                        { l: "Outstanding AR", v: "KYD 8,250", c: "text-coral-dark" },
                                        { l: "Bills due", v: "KYD 1,560", c: "text-ocean-700" },
                                        { l: "Net income", v: "KYD 6,820", c: "text-secondary-dark" },
                                    ].map((kpi) => (
                                        <div key={kpi.l} className="rounded-2xl border border-sand-200 p-4">
                                            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{kpi.l}</div>
                                            <div className={`mt-1 font-heading text-lg ${kpi.c}`}>{kpi.v}</div>
                                        </div>
                                    ))}
                                </div>
                                <div className="px-5 pb-5">
                                    <div className="rounded-2xl border border-sand-200 divide-y divide-sand-200">
                                        {[
                                            { n: "INV-00021", c: "Pirate's Cove Resort", a: "KYD 4,800.00", s: "Paid" },
                                            { n: "INV-00020", c: "Stingray Tours Ltd", a: "KYD 3,200.00", s: "Sent" },
                                            { n: "INV-00019", c: "Reef Watersports", a: "KYD 1,850.00", s: "Overdue" },
                                        ].map((r) => (
                                            <div key={r.n} className="flex items-center justify-between px-4 py-3">
                                                <div>
                                                    <div className="text-sm font-medium text-ocean-700">{r.c}</div>
                                                    <div className="text-xs text-muted-foreground">{r.n}</div>
                                                </div>
                                                <div className="text-sm font-mono">{r.a}</div>
                                                <Badge className={`border-0 ${
                                                    r.s === "Paid" ? "bg-emerald-50 text-emerald-700" :
                                                    r.s === "Sent" ? "bg-secondary/10 text-secondary-dark" :
                                                    "bg-coral/10 text-coral-dark"
                                                }`}>{r.s}</Badge>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="max-w-7xl mx-auto px-6 py-24">
                <div className="max-w-2xl">
                    <div className="text-xs uppercase tracking-[0.2em] text-secondary-dark font-semibold">Everything you need</div>
                    <h2 className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight text-ocean-700">A complete back office, distilled.</h2>
                    <p className="mt-4 text-muted-foreground">Sales, purchases, banking, accounting, and reporting — orchestrated in one calm interface.</p>
                </div>
                <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {FEATURES.map((f, i) => (
                        <motion.div
                            key={f.title}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: i * 0.05 }}
                            className="rounded-3xl bg-white border border-sand-200 p-7 shadow-card hover:shadow-card-hover transition-all"
                        >
                            <div className="h-11 w-11 rounded-2xl bg-ocean-50 text-secondary grid place-items-center">
                                <f.icon className="h-5 w-5" />
                            </div>
                            <h3 className="mt-5 font-heading text-xl text-ocean-700">{f.title}</h3>
                            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Cayman Section */}
            <section id="cayman" className="bg-deep-ocean text-white">
                <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-14 items-center">
                    <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-secondary-light font-semibold">Cayman jurisdiction support</div>
                        <h2 className="mt-3 font-heading text-4xl sm:text-5xl text-white tracking-tight">No corporate income tax. No friction.</h2>
                        <p className="mt-5 text-white/70 max-w-xl leading-relaxed">
                            BlueLedger ships with Cayman Islands as a first-class jurisdiction. Your invoices, reports, and reconciliation flows respect the local reality from day one — and gracefully support VAT or sales tax markets when you expand.
                        </p>
                        <ul className="mt-8 space-y-3 text-white/80">
                            {[
                                "Tax-exempt accounting mode toggle, on by default for KY companies",
                                "Zero-tax invoices with optional override per line item",
                                "Reports never force VAT/sales tax logic when no-tax mode is active",
                                "Multi-currency: KYD, USD, GBP, EUR — your call",
                                "Audit trail for every change to tax & jurisdiction settings",
                            ].map((t) => (
                                <li key={t} className="flex gap-3">
                                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-coral" />
                                    <span className="text-sm">{t}</span>
                                </li>
                            ))}
                        </ul>
                        <Link to="/pricing" className="inline-flex items-center mt-10 text-secondary-light font-medium hover:text-white transition-colors">
                            See how plans compare <ArrowRight className="h-4 w-4 ml-1.5" />
                        </Link>
                    </div>
                    <div className="relative">
                        <div className="rounded-3xl overflow-hidden border border-white/10 shadow-card">
                            <img src="https://images.unsplash.com/photo-1622561813774-01721ec19b53" alt="Cayman ocean" className="w-full h-[420px] object-cover" />
                        </div>
                        <div className="absolute -bottom-8 -left-8 bg-white text-ocean-700 rounded-2xl p-5 shadow-card max-w-xs">
                            <div className="text-xs uppercase tracking-[0.18em] text-secondary-dark font-semibold">Tax engine</div>
                            <div className="font-heading text-lg mt-1">Cayman Islands · No-tax mode</div>
                            <div className="text-xs text-muted-foreground mt-1">This company is configured as not subject to standard corporate income tax.</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* What's new — AI spotlight */}
            <section id="whats-new" className="max-w-7xl mx-auto px-6 py-24">
                <div className="grid lg:grid-cols-2 gap-14 items-center">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full bg-coral/10 text-coral-dark px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em]">
                            <Sparkles className="h-3.5 w-3.5" /> New
                        </div>
                        <h2 className="mt-4 font-heading text-4xl sm:text-5xl tracking-tight text-ocean-700">Scan a bill. Book a bill. That's it.</h2>
                        <p className="mt-5 text-muted-foreground max-w-xl leading-relaxed">
                            Drop in a supplier bill — PDF, phone photo, multi-page contract — and GPT-4o Vision reads the vendor, the dates, every line item, tax and totals. We fuzzy-match the vendor against your list (or create one), and open a pre-filled draft ready to save in one click. The file stays attached to the bill forever.
                        </p>
                        <ul className="mt-8 space-y-3 text-ocean-700">
                            {[
                                { i: ScanLine, t: "Multi-page PDFs supported — we stitch up to 5 pages into one legible image for the vision model." },
                                { i: Sparkles, t: "Same flow for expense receipts — snap, scan, book. Auto-category, auto-vendor, auto-date." },
                                { i: Coins, t: "Multi-currency with live FX. Enter USD, we fetch today's KYD rate, you can override, and the rate locks on save." },
                                { i: CheckSquare, t: "Bulk select & delete for invoices, bills, customers, vendors — with cascade-safe payment cleanup." },
                                { i: Paperclip, t: "Receipts on every money-out flow, compressed server-side and org-scoped for privacy." },
                            ].map((x) => (
                                <li key={x.t} className="flex gap-3">
                                    <x.i className="h-4 w-4 text-secondary mt-1 shrink-0" />
                                    <span className="text-sm leading-relaxed">{x.t}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="relative">
                        <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-coral/10 via-secondary/5 to-transparent blur-2xl" />
                        <div className="relative rounded-3xl bg-white border border-sand-200 shadow-card overflow-hidden">
                            <div className="bg-deep-ocean text-white px-6 py-4 flex items-center gap-3">
                                <Sparkles className="h-4 w-4 text-coral" />
                                <div className="text-sm font-medium">Scan bill with AI</div>
                            </div>
                            <div className="p-6 space-y-4">
                                <div className="rounded-2xl border-2 border-dashed border-sand-300 bg-sand-50/50 p-6 text-center">
                                    <Paperclip className="h-6 w-6 text-secondary mx-auto" />
                                    <div className="mt-2 text-sm font-medium text-ocean-700">ACME_Supplier_March.pdf</div>
                                    <div className="text-xs text-muted-foreground">1.4 MB · 2 pages · Ready to scan</div>
                                </div>
                                <div className="rounded-2xl bg-secondary/5 border border-secondary/20 p-4">
                                    <div className="text-xs uppercase tracking-wider text-secondary-dark font-semibold">Draft auto-filled</div>
                                    <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                                        <div>
                                            <div className="text-[11px] text-muted-foreground uppercase">Vendor</div>
                                            <div className="font-medium text-ocean-700">ACME Supplies Ltd</div>
                                        </div>
                                        <div>
                                            <div className="text-[11px] text-muted-foreground uppercase">Bill #</div>
                                            <div className="font-mono text-xs">INV-20260412</div>
                                        </div>
                                        <div>
                                            <div className="text-[11px] text-muted-foreground uppercase">Currency</div>
                                            <div className="font-mono">USD · 1.20</div>
                                        </div>
                                        <div>
                                            <div className="text-[11px] text-muted-foreground uppercase">Total</div>
                                            <div className="font-mono text-ocean-700 font-medium">$1,248.50</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                                    File stored org-scoped · rate locked on save
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Trust */}
            <section className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-3 gap-8">
                {[
                    { icon: Lock, t: "Tenant-isolated security", d: "Server-side permission enforcement, encrypted secrets, signed webhooks." },
                    { icon: Users, t: "Real RBAC, not theatre", d: "Owner, Admin, Accountant, Manager, Staff, Auditor — and custom roles." },
                    { icon: Zap, t: "Built to scale", d: "Multi-org, multi-currency, audit logs, and a tax engine that bends." },
                ].map((c) => (
                    <div key={c.t} className="rounded-3xl border border-sand-200 bg-white p-7 shadow-soft">
                        <c.icon className="h-6 w-6 text-secondary" />
                        <h3 className="mt-4 font-heading text-lg text-ocean-700">{c.t}</h3>
                        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{c.d}</p>
                    </div>
                ))}
            </section>

            {/* CTA */}
            <section className="max-w-7xl mx-auto px-6 pb-24">
                <div className="rounded-3xl bg-deep-ocean text-white p-12 lg:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 shadow-card">
                    <div>
                        <h3 className="font-heading text-3xl sm:text-4xl tracking-tight">Open a clean set of books today.</h3>
                        <p className="mt-3 text-white/70 max-w-xl">Start free. Upgrade when your business does. Cancel anytime — your data stays yours.</p>
                    </div>
                    <div className="flex gap-3">
                        <Link to="/register"><Button size="lg" className="bg-coral hover:bg-coral-dark text-white rounded-xl h-12 px-6" data-testid="footer-cta-start">Start free <ArrowRight className="h-4 w-4 ml-1.5" /></Button></Link>
                        <Link to="/pricing"><Button size="lg" variant="outline" className="rounded-xl h-12 px-6 border-white/30 bg-white/5 text-white hover:bg-white/10 hover:text-white">Compare plans</Button></Link>
                    </div>
                </div>
            </section>

            <footer className="border-t border-sand-200">
                <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
                    <Logo />
                    <div>© {new Date().getFullYear()} Modern Business Solutions Ltd · Made in the Cayman Islands</div>
                    <div className="flex gap-5">
                        <Link to="/pricing">Pricing</Link>
                        <Link to="/terms">Terms</Link>
                        <Link to="/privacy">Privacy</Link>
                        <Link to="/login">Sign in</Link>
                    </div>
                </div>
            </footer>
            <SalesAgentWidget />
        </div>
    );
}
